import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import ProductCard from "../components/ProductCard";
import { getProduct, getProducts } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import Seo from "../components/Seo";
import { buildAbsoluteUrl, getSiteUrl } from "../lib/seo";
import { calculateDiscountPercent } from "../lib/pricing";

const FREE_SHIPPING_MIN = 200000;
const WHATSAPP_LINK_BASE = "https://wa.me/573184215616";
const MEN_SIZES = [
  { us: "7.0", eu: "40", cm: "25.0" },
  { us: "8.0", eu: "41", cm: "26.0" },
  { us: "9.0", eu: "42", cm: "27.0" },
  { us: "10.0", eu: "43", cm: "28.0" },
  { us: "11.0", eu: "44", cm: "29.0" },
  { us: "12.0", eu: "45", cm: "30.0" },
];
const WOMEN_SIZES = [
  { us: "5.0", eu: "35", cm: "22.0" },
  { us: "6.0", eu: "36", cm: "23.0" },
  { us: "7.0", eu: "37.5", cm: "24.0" },
  { us: "8.0", eu: "39", cm: "25.0" },
  { us: "9.0", eu: "40.5", cm: "26.0" },
  { us: "10.0", eu: "42", cm: "27.0" },
];
const LEGACY_EQUIVALENCES = {
  "5": "35",
  "6": "36",
  "7": "40",
  "8": "41",
  "9": "42",
  "10": "43",
  "11": "44",
  "12": "45",
};
const MEN_SIZE_EQUIVALENCES = MEN_SIZES.reduce((acc, size) => {
  acc[size.us] = size.eu;
  return acc;
}, {});
const WOMEN_SIZE_EQUIVALENCES = WOMEN_SIZES.reduce((acc, size) => {
  acc[size.us] = size.eu;
  return acc;
}, {});
const SIZE_EQUIVALENCES = {
  ...LEGACY_EQUIVALENCES,
  ...MEN_SIZE_EQUIVALENCES,
  ...WOMEN_SIZE_EQUIVALENCES,
};

const getSizeEquivalence = (size, category) => {
  if (category === "mujer") {
    return WOMEN_SIZE_EQUIVALENCES[size] || SIZE_EQUIVALENCES[size] || null;
  }
  if (category === "hombre") {
    return MEN_SIZE_EQUIVALENCES[size] || SIZE_EQUIVALENCES[size] || null;
  }
  if (category === "unisex") {
    return (
      MEN_SIZE_EQUIVALENCES[size] ||
      WOMEN_SIZE_EQUIVALENCES[size] ||
      SIZE_EQUIVALENCES[size] ||
      null
    );
  }
  return SIZE_EQUIVALENCES[size] || null;
};

const parseSizeLabel = (value) => {
  if (!value) return { us: null, eu: null, cm: null };
  const text = String(value);
  const usMatch = text.match(/US\s*([0-9./]+)/i);
  const euMatch = text.match(/EUR\s*([0-9./]+)/i);
  const cmMatch = text.match(/CM\s*([0-9./]+)/i);
  return {
    us: usMatch ? usMatch[1] : null,
    eu: euMatch ? euMatch[1] : null,
    cm: cmMatch ? cmMatch[1] : null,
  };
};

const normalizeSizes = (sizes) => {
  if (!sizes) return [];
  if (Array.isArray(sizes)) {
    return sizes.map((size) => String(size).trim()).filter(Boolean);
  }
  if (typeof sizes === "string") {
    return sizes
      .split(",")
      .map((size) => size.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeColorOptions = (colors) => {
  if (!colors) return [];
  const raw = Array.isArray(colors) ? colors : String(colors).split(",");
  const seen = new Set();
  const values = [];
  raw.forEach((item) => {
    const cleaned = String(item || "").trim();
    if (!cleaned) return;
    const key = cleaned.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    values.push(cleaned);
  });
  return values;
};

const buildColorOptions = (colors, imageItems = []) => {
  const seen = new Set();
  const values = [];

  normalizeColorOptions(colors).forEach((color) => {
    const key = color.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    values.push(color);
  });

  imageItems.forEach((item) => {
    const color = String(item?.resolvedColor || item?.color || "").trim();
    if (!color) return;
    const key = color.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    values.push(color);
  });

  return values;
};

const findImageForColor = (imageItems, color) => {
  if (!color) return null;
  const normalizedColor = String(color).trim().toLowerCase();
  return (
    imageItems.find(
      (item) =>
        String(item?.resolvedColor || item?.color || "").trim().toLowerCase() ===
        normalizedColor,
    ) || null
  );
};

const findColorForImage = (imageItems, imageUrl) => {
  if (!imageUrl) return "";
  const match = imageItems.find((item) => item.url === imageUrl);
  return match?.resolvedColor || match?.color || "";
};

const normalizeImageItems = (productData) => {
  const productColors = normalizeColorOptions(productData?.color);
  const buildResolvedItem = (item, index) => {
    const explicitColor = item?.color ? String(item.color).trim() : "";
    const fallbackColor =
      productColors[index] ||
      (productColors.length === 1 ? productColors[0] : "");
    return {
      url: String(item?.url || "").trim(),
      color: explicitColor || null,
      resolvedColor: explicitColor || fallbackColor || null,
    };
  };
  const rawItems = Array.isArray(productData?.image_items)
    ? productData.image_items
    : [];
  const normalizedRaw = rawItems
    .map((item, index) => {
      if (typeof item === "string") {
        return buildResolvedItem({ url: item, color: null }, index);
      }
      return buildResolvedItem(item, index);
    })
    .filter((item) => item.url);
  if (normalizedRaw.length) return normalizedRaw;
  const fallback = Array.isArray(productData?.images) ? productData.images : [];
  return fallback
    .map((url, index) => buildResolvedItem({ url, color: null }, index))
    .filter((item) => item.url);
};

const buildSizeOptions = (sizes, category) => {
  const normalized = normalizeSizes(sizes);
  if (!normalized.length) {
    return [];
  }
  return normalized.map((size) => {
    const parsed = parseSizeLabel(size);
    const us = parsed.us || size;
    const eu = parsed.eu || getSizeEquivalence(us, category);
    return {
      value: size,
      us,
      eu,
      cm: parsed.cm || null,
    };
  });
};

const buildWhatsappLink = (message) => {
  if (!message) return WHATSAPP_LINK_BASE;
  return `${WHATSAPP_LINK_BASE}?text=${encodeURIComponent(message)}`;
};

const ProductoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [producto, setProducto] = useState(null);
  const [relacionados, setRelacionados] = useState([]);
  const [cantidad, setCantidad] = useState(1);
  const [talla, setTalla] = useState("");
  const [colorSeleccionado, setColorSeleccionado] = useState("");
  const [activeImage, setActiveImage] = useState("");
  const [loading, setLoading] = useState(false);
  const siteUrl = getSiteUrl();
  const canonicalUrl = siteUrl ? `${siteUrl}/producto/${id}` : undefined;

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    const load = async () => {
      try {
        const data = await getProduct(id);
        if (!isMounted) return;
        setProducto(data);
        const imageItems = normalizeImageItems(data);
        const imagesList = imageItems.map((item) => item.url);
        setCantidad(1);
        const isAccesorio = data.category === "accesorios";
        const options = isAccesorio ? [] : buildSizeOptions(data.sizes, data.category);
        setTalla(options[0]?.value || "");
        const colorOptions = buildColorOptions(data.color, imageItems);
        const initialColor = colorOptions[0] || imageItems[0]?.color || "";
        setColorSeleccionado(initialColor);
        const initialColorImage = findImageForColor(imageItems, initialColor);
        setActiveImage(initialColorImage?.url || imagesList[0] || data.image_url || "");
        try {
          const relacionadosData = await getProducts({
            category: data.category,
            limit: 8,
          });
          if (isMounted) {
            const relacionadosFiltrados = relacionadosData
              .filter((item) => item.id !== Number(id))
              .slice(0, 4);
            setRelacionados(relacionadosFiltrados);
          }
        } catch {
          if (isMounted) {
            setRelacionados([]);
          }
        }
      } catch {
        if (isMounted) {
          setProducto(null);
          setRelacionados([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const formatoPrecio = (precio) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(precio);
  };

  const handleSelectImage = (imageItem) => {
    if (!imageItem?.url) return;
    setActiveImage(imageItem.url);
    const relatedColor = imageItem.resolvedColor || imageItem.color || "";
    if (relatedColor) {
      setColorSeleccionado(relatedColor);
    }
  };

  const handleAgregarCarrito = () => {
    if (!producto) {
      return;
    }
    const availableStock = Number(producto.stock ?? 0);
    if (availableStock <= 0) {
      toast({
        title: "Sin stock",
        description: "Este producto no tiene unidades disponibles.",
        variant: "destructive",
      });
      return;
    }
    const requiresSize = producto.category !== "accesorios";
    if (requiresSize && !sizeOptions.length) {
      toast({
        title: "Sin tallas configuradas",
        description: "Este producto aun no tiene tallas registradas para compra directa.",
      });
      return;
    }
    if (requiresSize && !talla) {
      toast({
        title: "Selecciona una talla",
        description: "Debes elegir una talla antes de agregar al carrito.",
      });
      return;
    }
    const colorOptions = buildColorOptions(producto.color, imageItems);
    if (colorOptions.length && !selectedColorLabel) {
      toast({
        title: "Selecciona un color",
        description: "Debes elegir un color antes de agregar al carrito.",
      });
      return;
    }
    if (cantidad > availableStock) {
      toast({
        title: "Stock insuficiente",
        description: `Solo hay ${availableStock} unidades disponibles.`,
        variant: "destructive",
      });
      return;
    }

    const selectedOption = sizeOptions.find((option) => option.value === talla);
    const parsedSize = parseSizeLabel(talla);
    const tallaEu = talla
      ? selectedOption?.eu || parsedSize.eu || getSizeEquivalence(parsedSize.us || talla, producto.category)
      : null;
    const carrito = JSON.parse(localStorage.getItem("carrito") || "[]");
    const item = {
      productoId: producto.id,
      nombre: producto.name,
      imagen: activeImage || producto.image_url,
      precio: producto.sale_price || producto.price,
      cantidad,
      talla: requiresSize ? talla : null,
      tallaEu,
      color: selectedColorLabel || null,
    };

    const existente = carrito.findIndex(
      (i) =>
        i.productoId === item.productoId &&
        i.talla === item.talla &&
        (i.color || null) === (item.color || null),
    );
    const existenteCantidad = existente !== -1 ? carrito[existente].cantidad : 0;
    if (existenteCantidad + cantidad > availableStock) {
      toast({
        title: "Stock insuficiente",
        description: `Solo hay ${availableStock} unidades disponibles.`,
        variant: "destructive",
      });
      return;
    }
    if (existente !== -1) {
      carrito[existente].cantidad += cantidad;
    } else {
      carrito.push(item);
    }

    localStorage.setItem("carrito", JSON.stringify(carrito));
    toast({
      title: "Agregado al carrito",
      description: `${producto.name} se agrego correctamente`,
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 text-center text-gray-500">
        <Seo title="Cargando producto" robots="noindex, nofollow" />
        Cargando producto...
      </div>
    );
  }

  if (!producto) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <Seo title="Producto no encontrado" robots="noindex, nofollow" />
        <h2 className="text-2xl font-bold mb-4">Producto no encontrado</h2>
        <Button onClick={() => navigate("/catalogo/todos")}>Ver catalogo</Button>
      </div>
    );
  }

  const categoriaLabel = `${producto.category.charAt(0).toUpperCase()}${producto.category.slice(1)}`;
  const priceValue = Number(producto.sale_price ?? producto.price ?? 0);
  const discountPercent = calculateDiscountPercent(producto.price, producto.sale_price);
  const productDescription = producto.description || "Tenis y zapatillas en Colombia.";
  const imageItems = normalizeImageItems(producto);
  const imageGallery = imageItems.length
    ? imageItems.map((item) => item.url)
    : [producto.image_url].filter(Boolean);
  const galleryAbsolute = imageGallery
    .map((image) => buildAbsoluteUrl(image))
    .filter(Boolean);
  const productImage = galleryAbsolute[0] || buildAbsoluteUrl(producto.image_url);
  const isAccesorio = producto.category === "accesorios";
  const isWhatsappOnly = Boolean(producto.whatsapp_only);
  const qualifiesFreeShipping = priceValue >= FREE_SHIPPING_MIN;
  const colorOptions = buildColorOptions(producto.color, imageItems);
  const sizeOptions = isAccesorio
    ? []
    : buildSizeOptions(producto.sizes, producto.category);
  const hasConfiguredSizes = isAccesorio || sizeOptions.length > 0;
  const activeImageColor = findColorForImage(imageItems, activeImage || producto.image_url);
  const selectedColorLabel = colorSeleccionado || activeImageColor || "";
  const whatsappMessageParts = [
    "Hola, quiero comprar por WhatsApp:",
    producto.name,
    `Cantidad: ${cantidad}`,
    `Foto: ${activeImage || producto.image_url}`,
  ];
  if (!isAccesorio && talla) {
    whatsappMessageParts.push(`Talla: ${talla}`);
  }
  const whatsappMessage = whatsappMessageParts.filter(Boolean).join("\n");
  const whatsappLink = buildWhatsappLink(whatsappMessage);
  const creditMessageParts = [
    "Hola, quiero mi credito. Estoy interesado en este producto:",
    producto.name,
    `Foto: ${activeImage || producto.image_url}`,
  ];
  if (!isAccesorio && talla) {
    creditMessageParts.push(`Talla: ${talla}`);
  }
  const creditWhatsappLink = buildWhatsappLink(creditMessageParts.join("\n"));
  const productJsonLd = siteUrl
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: producto.name,
        image: galleryAbsolute.length ? galleryAbsolute : productImage ? [productImage] : undefined,
        description: productDescription,
        sku: String(producto.id),
        ...(producto.brand
          ? { brand: { "@type": "Brand", name: producto.brand } }
          : {}),
        offers: {
          "@type": "Offer",
          url: canonicalUrl,
          priceCurrency: "COP",
          price: String(priceValue),
          availability: "https://schema.org/InStock",
          itemCondition: "https://schema.org/NewCondition",
        },
      }
    : undefined;
  const breadcrumbJsonLd = siteUrl
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Inicio",
            item: `${siteUrl}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Catalogo",
            item: `${siteUrl}/catalogo/todos`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: categoriaLabel,
            item: `${siteUrl}/catalogo/${producto.category}`,
          },
          {
            "@type": "ListItem",
            position: 4,
            name: producto.name,
            item: canonicalUrl,
          },
        ],
      }
    : undefined;
  const seoJsonLd = siteUrl
    ? [productJsonLd, breadcrumbJsonLd].filter(Boolean)
    : undefined;

  return (
    <div className="min-h-screen">
      <Seo
        title={producto.name}
        description={productDescription}
        canonical={canonicalUrl}
        image={productImage}
        type="product"
        jsonLd={seoJsonLd}
      />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <p className="text-sm text-gray-600">
            Inicio / {categoriaLabel} /{" "}
            {producto.name}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div>
            <div className="aspect-square w-full max-w-[480px] mx-auto md:mx-0 mb-4 overflow-hidden rounded-lg bg-white">
              <img
                src={activeImage || producto.image_url}
                alt={producto.name}
                className="w-full h-full object-contain"
              />
            </div>
            {imageGallery.length > 1 && (
              <div className="flex flex-wrap gap-3">
                {imageItems.map((imageItem, index) => (
                  <button
                    key={`${imageItem.url}-${index}`}
                    type="button"
                    className={`h-16 w-16 overflow-hidden rounded-md border ${
                      (activeImage || producto.image_url) === imageItem.url
                        ? "border-black"
                        : "border-gray-200"
                    }`}
                    onClick={() => handleSelectImage(imageItem)}
                    title={producto.name}
                  >
                    <img
                      src={imageItem.url}
                      alt={producto.name}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
            {imageGallery.length > 1 && (
              <p className="mt-5 w-full max-w-[480px] mx-auto text-sm text-gray-600 md:mx-0">
                Selecciona la foto que quieres ver para elegir la variante del producto.
              </p>
            )}
          </div>

          <div>
            <div className="mb-4">
              <h1 className="text-3xl font-bold mb-2">{producto.name}</h1>
              <p className="text-sm text-gray-600 mb-4 capitalize">{producto.category}</p>
              {producto.brand && (
                <p className="text-sm text-gray-600 mb-1">Marca: {producto.brand}</p>
              )}
              <p className="text-sm text-gray-600 mb-4">
                {producto.stock ?? 0} disponibles
              </p>

              <div className="mb-6">
                {producto.sale_price ? (
                  <>
                    <span className="text-gray-400 line-through text-xl mr-3">
                      {formatoPrecio(producto.price)}
                    </span>
                    <span className="text-3xl font-bold text-red-600">
                      {formatoPrecio(producto.sale_price)}
                    </span>
                    {discountPercent > 0 && (
                      <span className="ml-3 inline-flex items-center rounded-full bg-red-50 px-3 py-1 text-sm font-semibold text-red-700">
                        -{discountPercent}% OFF
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-3xl font-bold">
                    {formatoPrecio(producto.price)}
                  </span>
                )}
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">
                {producto.description || "Descripcion no disponible."}
              </p>
              {qualifiesFreeShipping && (
                <span className="mt-2 inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  Envio gratis
                </span>
              )}
              {isWhatsappOnly && (
                <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-sm font-semibold leading-6 text-gray-800">
                    Este producto es de alto flujo. Para confirmar la disponibilidad y tallas en
                    tiempo real, por favor verifica con un asesor.
                  </p>
                </div>
              )}
            </div>

            <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">
                Tu seleccion
              </p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <p className="text-xs text-gray-500">Vista seleccionada</p>
                  <div className="mt-2 h-20 w-20 overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <img
                      src={activeImage || producto.image_url}
                      alt={producto.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
                {!isAccesorio && (
                  <div>
                    <p className="text-xs text-gray-500">Talla</p>
                    <p className="font-semibold">
                      {hasConfiguredSizes ? talla || "Selecciona una talla" : "Sin tallas cargadas"}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500">Cantidad</p>
                  <p className="font-semibold">{cantidad}</p>
                </div>
              </div>
              {imageGallery.length > 1 && (
                <p className="mt-4 border-t border-gray-200 pt-4 text-xs text-gray-500">
                  Puedes cambiar la variante tocando cualquiera de las miniaturas.
                </p>
              )}
            </div>

            {!isAccesorio && (
              <div className="mb-6">
                <Label className="block mb-2 font-semibold">Talla US / EUR:</Label>
                {sizeOptions.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {sizeOptions.map((size) => (
                      <div key={size.value} className="flex flex-col items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setTalla(size.value)}
                          className={`h-10 min-w-[2.75rem] whitespace-nowrap rounded-full border px-3 text-sm font-semibold transition ${
                            talla === size.value
                              ? "border-black bg-black text-white"
                              : "border-gray-300 text-gray-700 hover:border-black"
                          }`}
                        >
                          {size.us}
                        </button>
                        {size.eu && (
                          <span className="text-xs text-gray-500">EUR {size.eu}</span>
                        )}
                        {size.cm && (
                          <span className="text-xs text-gray-500">CM {size.cm}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Este producto aun no tiene tallas configuradas. Puedes pedirlo por WhatsApp
                    mientras el admin registra el tallaje.
                  </p>
                )}
              </div>
            )}

            <div className="mb-6">
              <Label className="block mb-2 font-semibold">Cantidad:</Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCantidad(Math.max(1, cantidad - 1))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-12 text-center font-semibold">{cantidad}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCantidad(cantidad + 1)}
                  disabled={
                    Number(producto.stock ?? 0) > 0 &&
                    cantidad >= Number(producto.stock ?? 0)
                  }
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {Number(producto.stock ?? 0) <= 0 && (
                <p className="text-xs text-red-600 mt-2">Producto agotado.</p>
              )}
            </div>

            <div className="space-y-3">
              {!isWhatsappOnly && (
                <Button
                  size="lg"
                  className="w-full"
                  onClick={handleAgregarCarrito}
                  disabled={Number(producto.stock ?? 0) <= 0 || !hasConfiguredSizes}
                >
                  Agregar al carrito
                </Button>
              )}
              <Button
                size="lg"
                variant={isWhatsappOnly ? "default" : "outline"}
                className={`w-full ${
                  isWhatsappOnly
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                }`}
                onClick={() =>
                  window.open(whatsappLink, "_blank", "noopener,noreferrer")
                }
              >
                Asesoria y Compra por WhatsApp
              </Button>
              <Button
                size="lg"
                className="w-full bg-emerald-600 text-white hover:bg-emerald-700"
                onClick={() =>
                  window.open(creditWhatsappLink, "_blank", "noopener,noreferrer")
                }
              >
                Quiero mi credito
              </Button>
            </div>
          </div>
        </div>

        {relacionados.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold mb-6">Productos relacionados</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relacionados.map((item) => (
                <ProductCard key={item.id} producto={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

const Label = ({ children, className }) => (
  <label className={className}>{children}</label>
);

export default ProductoDetalle;
